import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatchBreakdown, MatchBreakdownGrid, MatchBreakdownRequirement } from './models/match-breakdown.model';
import { SubmittalJobQualifications } from 'src/app/recruiting/candidates/submittal/models/submittal-job-qualifications.model';
import { defer, Observable, map } from 'rxjs';
import { SubmittalJobQualificationsUnit } from 'src/app/recruiting/candidates/submittal/models/submittal-job-qualifications-unit.model';
import { SubmittalJobMatchCalculationTypes } from 'src/app/recruiting/candidates/submittal/models/submittal-job-match-calculation-types.model';
import { MatDialog } from '@angular/material/dialog';
import { MatchBreakdownDialogComponent } from './match-breakdown-dialog/match-breakdown-dialog.component';
import { JobMatchingService } from 'src/app/shared/services/job-matching.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { applyJobSpecificMatch } from 'src/app/shared/helpers/job-qualification-requirement-helper';

@Injectable({
    providedIn: 'root'
})
export class MatchBreakdownService {
    private readonly _dialog = inject(MatDialog);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _jobMatchingService: JobMatchingService = inject(JobMatchingService);
    private readonly _ldFeatureManager: LDFeatureManager = inject(LDFeatureManager);

    calculationTypeEnum = SubmittalJobMatchCalculationTypes;
    isAltCertsEnabled = this._ldFeatureManager.isEnabledSignal(FeatureFlag.AltCertsMatchBreakdown);

    openMatchBreakdownDialog(
        submittalId: number,
        candidateId: number,
        jobId: number,
        candidateName?: string,
        matchCategory?: string,
        jobTitle?: string
    ) {
        this._dialog.open(MatchBreakdownDialogComponent, {
            data: {
                submittal: {
                    submittalId: +submittalId,
                    candidateUserId: +candidateId,
                    jobId: +jobId
                },
                uiLabels: {
                    jobTitle: jobTitle,
                    candidateName: candidateName,
                    matchCategory: matchCategory,
                    hideReviewNotes: true
                }
            },
            maxWidth: 900,
            minWidth: 900,
            panelClass: ['nova__dialog']
        });
    }

    openMatchBreakdownDialogWithContinue(
        submittalId: number,
        candidateId: number,
        jobId: number,
        continueCallBackFunc: () => void,
        isContinueEnabled: boolean,
        candidateName?: string,
        matchCategory?: string,
        jobTitle?: string
    ) {
        this._dialog.open(MatchBreakdownDialogComponent, {
            data: {
                submittal: {
                    submittalId: +submittalId,
                    candidateUserId: +candidateId,
                    jobId: +jobId,
                    continueCallBackFunc,
                    isContinueEnabled
                },
                uiLabels: {
                    jobTitle: jobTitle,
                    candidateName: candidateName,
                    matchCategory: matchCategory,
                    hideReviewNotes: false
                }
            },
            maxWidth: 900,
            minWidth: 900,
            panelClass: ['nova__dialog']
        });
    }

    openMatchBreakdownDialogForJob(
        matches: SubmittalJobQualifications,
        candidateUserId: number,
        jobId: number,
        candidateName?: string,
        hideReviewNotes?: boolean
    ) {
        const matchBreakdown = this.getMatchBreakdownForQualifications(matches, candidateUserId, jobId);

        this._dialog.open(MatchBreakdownDialogComponent, {
            data: {
                job: {
                    jobId: +jobId,
                    matchBreakdown,
                    isJobDetailsOnly:
                        matchBreakdown.calculationType == null || matchBreakdown.calculationType === undefined
                },
                uiLabels: {
                    candidateName: candidateName,
                    matchCategory: matches?.calculations.find((calc) => calc.jobId === jobId)?.matchCategory,
                    hideReviewNotes: hideReviewNotes
                }
            },
            maxWidth: 800,
            minWidth: 800,
            panelClass: ['nova__dialog', 'nova__dialog--md']
        });
    }

    getMatchBreakdown(candidateUserId: number, jobId: number): Observable<MatchBreakdown> {
        return this._jobMatchingService.getQualifications(candidateUserId, [jobId]).pipe(
            takeUntilDestroyed(this._destroyRef),
            map((matches) => this.getMatchBreakdownForQualifications(matches, candidateUserId, jobId))
        );
    }

    getMatchBreakdownForSubmittal = (
        submittalId: number,
        candidateUserId: number,
        jobId: number
    ): Observable<MatchBreakdown> =>
        this._jobMatchingService.getCalculationsAndExplanations(submittalId, candidateUserId, jobId).pipe(
            takeUntilDestroyed(this._destroyRef),
            map((matches) => this.getMatchBreakdownForQualifications(matches, candidateUserId, jobId))
        );

    getMatchBreakdownForQualifications(
        matches: SubmittalJobQualifications,
        candidateUserId: number,
        jobId: number
    ): MatchBreakdown {
        jobId = +jobId;
        candidateUserId = +candidateUserId;
        const calculation = matches?.calculations.find((calc) => calc.jobId === jobId);
        const unitDesc = matches?.unitDescriptions.find((unit) => unit.jobId === jobId);
        return {
            percentageMatch: calculation?.matchPercentage ?? 0,
            matchCategory: calculation?.matchCategory ?? null,
            matchColor: calculation?.matchColor ?? null,
            candidateId: candidateUserId,
            unitFormDescriptionId: unitDesc?.unitDescriptionFormId ?? null,
            unitName: unitDesc?.unitName ?? '',
            facilityId: unitDesc?.facilityId ?? null,
            facilityName: unitDesc?.facilityName ?? '',
            userCompletedAssessment: unitDesc?.userCompletedAssessment ?? false,
            jobTrackId: unitDesc?.jobTrackId ?? null,
            jobTrackName: unitDesc?.jobTrackName ?? '',
            unitTrackId: unitDesc?.unitTrackId ?? null,
            unitTrackName: unitDesc?.unitTrackName ?? '',
            calculationType: unitDesc?.calculationType ?? null,
            hasJobLevelEdits: unitDesc?.hasJobLevelEdits ?? false,
            requiredQualifications: this.getRequiredQualifications(matches, candidateUserId, jobId)
        };
    }

    getRequiredQualifications(matches: SubmittalJobQualifications, candidateUserId: number, jobId: number) {
        jobId = +jobId;
        candidateUserId = +candidateUserId;
        const requirements: MatchBreakdownGrid[] = [];
        requirements.push(this.getLicenseRequirements(matches, candidateUserId, jobId));
        requirements.push(this.getCertifications(matches, candidateUserId, jobId));
        requirements.push(this.getSkills(matches, candidateUserId, jobId));
        requirements.push(this.getWorkHistoryRequirements(matches, candidateUserId, jobId));
        requirements.push(this.getUnitDescription(matches, candidateUserId, jobId));
        return requirements;
    }

    getCertifications(matches: SubmittalJobQualifications, candidateUserId: number, jobId: number): MatchBreakdownGrid {
        jobId = +jobId;
        candidateUserId = +candidateUserId;
        const certifications = (matches?.certificationRequirements ?? [])
            .filter((req) => Object.keys(req.impactedJobs).map(Number).includes(jobId))
            .map((req) => applyJobSpecificMatch(req, jobId));
        matches?.otherCertifications.forEach((other) => {
            if (!certifications?.some((req) => req.name === other.name && req.isOther) && other.impactedJobs[jobId]) {
                certifications.push({
                    id: -1,
                    name: other.name,
                    candidateHasMetRequirement: false,
                    impactedJobs: other.impactedJobs,
                    explanation: null,
                    workHistoryGap: null,
                    isOther: true,
                    isRequirementExpired: false,
                    isRequirementMissing: false,
                    isCandidateExpirationDateMissing: false,
                    isCandidateValueLowRating: false,
                    jobsWithAddedRequirements: other.jobsWithAddedRequirements ?? []
                });
            }
        });
        const requirements = (certifications ?? []).map(
            (r) => new MatchBreakdownRequirement(r, this.isAltCertsEnabled(), jobId)
        );
        requirements.forEach(
            (r) => (r.isAdditionalRequirement = r.jobsWithAddedRequirements?.includes(jobId) ?? false)
        );

        return {
            title: 'Required Certifications',
            header: 'Required Certifications',
            warning: false,
            subtitle: `The following matches are based on the certifications listed in the
            <a class="nova__link" href="/#/recruiting/candidates/${candidateUserId}/new-profile" target="_blank" rel="noopener">
                candidate's profile
            </a>.${
                this.isAltCertsEnabled()
                    ? '<br><br>Some certifications have alternative options. These alternatives meet the criteria and fulfill the requirements of the primary certification.'
                    : ''
            }`,
            candidateId: candidateUserId,
            jobId: jobId,
            hasAdditionalRequirements: requirements.some((r) => r.isAdditionalRequirement),
            requirements$: defer(() =>
                Promise.resolve({
                    data: requirements,
                    total: requirements.length
                })
            )
        };
    }

    getSkills(matches: SubmittalJobQualifications, candidateUserId: number, jobId: number): MatchBreakdownGrid {
        jobId = +jobId;
        candidateUserId = +candidateUserId;
        const unitDesc = matches?.unitDescriptions.find((unit) => unit.jobId === jobId);
        const skills = matches?.skillRequirements?.filter((req) =>
            Object.keys(req.impactedJobs).map(Number).includes(jobId)
        );
        matches?.otherSkills.forEach((other) => {
            if (!skills?.some((req) => req.name === other.name && req.isOther) && other.impactedJobs[jobId]) {
                skills.push({
                    id: -1,
                    name: other.name,
                    candidateHasMetRequirement: false,
                    impactedJobs: other.impactedJobs,
                    categoryName: other.categoryName,
                    explanation: null,
                    workHistoryGap: null,
                    isOther: true,
                    isRequirementExpired: false,
                    isRequirementMissing: false,
                    isCandidateExpirationDateMissing: false,
                    isCandidateValueLowRating: false,
                    jobsWithAddedRequirements: other.jobsWithAddedRequirements ?? []
                });
            }
        });
        const requirements = (skills ?? []).map((r) => new MatchBreakdownRequirement(r, false, jobId));
        requirements.forEach(
            (r) => (r.isAdditionalRequirement = r.jobsWithAddedRequirements?.includes(jobId) ?? false)
        );
        return {
            title: 'Required Skills',
            header: 'Required Skills',
            warning: true,
            userCompletedAssessment: unitDesc?.userCompletedAssessment ?? false,
            isSkills: true,
            subtitle: this.getSkillsSubTitle(unitDesc, candidateUserId),
            otherAssessmentNote: this.getOtherAssessmentNote(unitDesc),
            candidateId: candidateUserId,
            jobId: jobId,
            hasAdditionalRequirements: requirements.some((r) => r.isAdditionalRequirement),
            requirements$: defer(() =>
                Promise.resolve({
                    data: requirements,
                    total: requirements.length
                })
            )
        };
    }

    getWorkHistoryRequirements(
        matches: SubmittalJobQualifications,
        candidateUserId: number,
        jobId: number
    ): MatchBreakdownGrid {
        jobId = +jobId;
        candidateUserId = +candidateUserId;
        const workHistory = matches?.workRequirements?.filter((req) =>
            Object.keys(req.impactedJobs).map(Number).includes(jobId)
        );
        const requirements = (workHistory ?? []).map((r) => new MatchBreakdownRequirement(r, false, jobId));
        requirements.forEach(
            (r) => (r.isAdditionalRequirement = r.jobsWithAddedRequirements?.includes(jobId) ?? false)
        );

        return {
            title: 'Required Work Experience',
            header: 'Required Work Experience',
            warning: false,
            subtitle: this.getWorkHistoryExperienceSubtitle(candidateUserId),
            candidateId: candidateUserId,
            jobId: jobId,
            hasAdditionalRequirements: requirements.some((r) => r.isAdditionalRequirement),
            requirements$: defer(() =>
                Promise.resolve({
                    data: requirements,
                    total: requirements.length
                })
            )
        };
    }

    getUnitDescription(
        matches: SubmittalJobQualifications,
        candidateUserId: number,
        jobId: number
    ): MatchBreakdownGrid {
        candidateUserId = +candidateUserId;
        const items = [];
        const unitAcceptsFirstTimeTravelerNote = matches?.unitDescriptions
            ?.map((d) => d?.unitAcceptsFirstTimeTravelerNote)
            ?.filter(Boolean);
        if (unitAcceptsFirstTimeTravelerNote?.length) {
            items.push({ name: 'Unit Accepts First Time Travelers', requirements: unitAcceptsFirstTimeTravelerNote });
        }
        const patientDiagnoses = matches?.unitDescriptions?.map((d) => d?.patientDiagnoses)?.filter(Boolean);
        const unitDetails = matches?.unitDescriptions?.map((d) => d?.unitDetails)?.filter(Boolean);
        const specialEquipment = matches?.unitDescriptions?.map((unit) => unit?.specialEquipment)?.filter(Boolean);

        if (patientDiagnoses?.length) {
            items.push({ name: 'Patient Diagnoses', requirements: patientDiagnoses });
        }

        if (unitDetails?.length) {
            items.push({ name: 'Special Procedures/Unit Details', requirements: unitDetails });
        }

        if (specialEquipment?.length) {
            items.push({ name: 'Special Equipment', requirements: specialEquipment });
        }

        return {
            title: 'Other Requirements',
            header: 'Requirement Area',
            isOther: true,
            subtitle: `The following requirements <strong>are not</strong> automatically matched from the candidate's profile.
                Please review to ensure that your candidate is qualified.`,
            candidateId: candidateUserId,
            jobId: jobId,
            requirements$: defer(() =>
                Promise.resolve({
                    data: items,
                    total: items?.length
                })
            )
        };
    }

    getLicenseRequirements(
        matches: SubmittalJobQualifications,
        candidateUserId: number,
        jobId: number
    ): MatchBreakdownGrid {
        jobId = +jobId;
        candidateUserId = +candidateUserId;
        const licenseRequirements = matches?.licenseRequirements?.filter((req) =>
            Object.keys(req.impactedJobs).map(Number).includes(jobId)
        );
        const requirements = (licenseRequirements ?? []).map((r) => new MatchBreakdownRequirement(r, false, jobId));
        requirements.forEach(
            (r) => (r.isAdditionalRequirement = r.jobsWithAddedRequirements?.includes(jobId) ?? false)
        );

        return {
            title: 'Required Licenses',
            header: 'Required Licenses',
            warning: false,
            subtitle: `The following matches are based on the licenses listed in the
                <a class="nova__link"
                    href="/#/recruiting/candidates/${candidateUserId}/new-profile/qualifications"
                    target="_blank" rel="noopener">
                    candidate's profile
                </a>`,
            candidateId: candidateUserId,
            jobId: jobId,
            hasAdditionalRequirements: requirements.some((r) => r.isAdditionalRequirement),
            requirements$: defer(() =>
                Promise.resolve({
                    data: requirements,
                    total: requirements.length
                })
            )
        };
    }

    getSkillsSubTitle(unitDesc: SubmittalJobQualificationsUnit, candidateId: number) {
        let typeText = 'for this unit.';
        if (unitDesc?.calculationType === this.calculationTypeEnum.JobTrack) {
            typeText = 'in the clinical track.';
        }
        return `These required skills are based on the <strong>${unitDesc?.assessmentName} assessment</strong>.
                    Matches are auto-populated from the
                    <a class="nova__link"
                        href="/#/recruiting/candidates/${candidateId}/new-profile/qualifications"
                        target="_blank" rel="noopener">
                        candidate's self assessment
                    </a>
                    in their profile.
                    Candidates must have a rating of 3 or 4 for each skill required ${typeText}`;
    }

    getOtherAssessmentNote(unitDesc: SubmittalJobQualificationsUnit) {
        return `Note: Certain skill requirements may have been matched from another assessment and are indicated below.
        Please make sure the <span style="font-weight: 700;">${unitDesc?.assessmentName} assessment</span> is up to date.`;
    }

    getWorkHistoryExperienceSubtitle(candidateId: number) {
        return `The following requirements for years of experience and proficiency matches are based on the
                <a class="nova__link"
                    href="/#/recruiting/candidates/${candidateId}/new-profile/work-history"
                    target="_blank" rel="noopener">
                    candidate's work history
                </a>
                and
                <a class="nova__link"
                    href="/#/recruiting/candidates/${candidateId}/new-profile/qualifications"
                    target="_blank" rel="noopener">
                    qualifications
                </a>.`;
    }
}
